// @flow @format

import { EMPTY, forkJoin, of } from 'rxjs';
import { catchError, map, mapTo, switchMap } from 'rxjs/operators';
import { combineEpics, ofType } from 'redux-observable';

import type { ActionType } from '../utils';
import type { ObservableAction, StateObservable } from './types';
import { catchWith } from './utils';
import { convertProgressFile } from '../../convertProgressFile';
import { fetchJsonFromS3, uploadJsonToS3 } from './s3Helper';
import {
  fetchProgressBegin,
  fetchProgressFailure,
  fetchProgressSuccess,
  progressUpdated,
  uploadProgressBegin,
  uploadProgressFailure,
  uploadProgressSuccess,
} from '../reducer/progresses';

export function fetchProgressEpic(action$: ObservableAction, state$: StateObservable): ObservableAction {
  return action$.pipe(
    ofType<ActionType<typeof fetchProgressBegin>>(fetchProgressBegin.type),
    switchMap(() =>
      fetchJsonFromS3(state$.value, 'progress.json').pipe(
        switchMap((progressFile: any) => {
          // convert if old format
          if (Object.keys(progressFile).length && !progressFile.version) {
            // backup current file
            const backup$ = uploadJsonToS3(state$.value, 'progress.json.v0', progressFile).pipe(
              map(_ => EMPTY),
              catchError(_ => EMPTY)
            );

            const newProgressFile = convertProgressFile(progressFile, state$.value.entities);
            // upload converted file
            const uploadConverted$ = uploadJsonToS3(state$.value, 'progress.json', newProgressFile).pipe(
              map(_ => EMPTY),
              catchError(_ => EMPTY)
            );

            const notifySuccessAction$ = of(fetchProgressSuccess(newProgressFile.progresses || []));

            // $FlowIgnore
            return forkJoin([backup$, uploadConverted$, notifySuccessAction$]).pipe(map(([, , action]) => action));
          } else {
            return of(fetchProgressSuccess(progressFile.progresses || []));
          }
        }),
        catchWith(fetchProgressFailure, undefined)
      )
    )
  );
}

function wrapMeta(progresses: any) {
  return {
    version: '20190520',
    progresses,
  };
}

export function uploadProgressEpic(action$: ObservableAction, state$: StateObservable): ObservableAction {
  return action$.pipe(
    ofType<ActionType<typeof uploadProgressBegin>>(uploadProgressBegin.type),
    switchMap(() =>
      uploadJsonToS3(state$.value, 'progress.json', wrapMeta(state$.value.progresses)).pipe(
        map(uploadProgressSuccess),
        catchWith(uploadProgressFailure, undefined)
      )
    )
  );
}

export function progressUpdatedEpic(action$: ObservableAction): ObservableAction {
  return action$.pipe(ofType<ActionType<typeof progressUpdated>>(progressUpdated.type), mapTo(uploadProgressBegin()));
}

export default combineEpics(fetchProgressEpic, uploadProgressEpic, progressUpdatedEpic);
